import { useEffect, useState } from 'react';
import { readError } from '../../app/api';
import { formatDateTime } from '../../shared/date-time';
import { AnalyticsFilterBar } from './AnalyticsFilterBar';
import type { AnalyticsFilterScope } from './AnalyticsFilterScope';
import {
  loadScopedInstallationPage,
  loadScopedInstallationSessionPage,
} from './analytics-api';
import { SessionCountry } from './GeoUi';
import { PaginatedSessionEventTimeline } from './PaginatedSessionEventTimeline';
import { PaginationControls } from './PaginationControls';
import { PlatformBadge, PlatformBadgeList } from './PlatformUi';
import type {
  InstallationPage,
  InstallationSummary,
  SessionPage,
  SessionSummary,
} from './types';

export function InstallationExplorerView(props: {
  environmentId: string;
  filters: AnalyticsFilterScope;
  onChangeFilters: (filters: AnalyticsFilterScope) => void;
}): React.JSX.Element {
  const [installationPage, setInstallationPage] = useState<InstallationPage | null>(null);
  const [installationPageNumber, setInstallationPageNumber] = useState(1);
  const [selectedInstallationId, setSelectedInstallationId] = useState<string | null>(null);
  const [sessionPage, setSessionPage] = useState<SessionPage | null>(null);
  const [sessionPageNumber, setSessionPageNumber] = useState(1);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [loadingInstallations, setLoadingInstallations] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setInstallationPageNumber(1);
    setSessionPageNumber(1);
  }, [props.environmentId]);

  useEffect(() => {
    let active = true;
    setLoadingInstallations(true);
    setError('');
    setInstallationPage(null);
    setSelectedInstallationId(null);
    loadScopedInstallationPage(props.environmentId, props.filters, installationPageNumber)
      .then((response) => {
        if (!active) return;
        setInstallationPage(response);
        if (response.pagination.page !== installationPageNumber) {
          setInstallationPageNumber(response.pagination.page);
        }
        setSelectedInstallationId(response.installations[0]?.installationId ?? null);
      })
      .catch((caught: unknown) => { if (active) setError(readError(caught)); })
      .finally(() => { if (active) setLoadingInstallations(false); });
    return () => { active = false; };
  }, [installationPageNumber, props.environmentId, props.filters]);

  useEffect(() => {
    let active = true;
    setSessionPage(null);
    setSelectedSessionId(null);
    if (selectedInstallationId === null) {
      setLoadingSessions(false);
      return () => { active = false; };
    }
    setLoadingSessions(true);
    setError('');
    loadScopedInstallationSessionPage(
      props.environmentId,
      selectedInstallationId,
      props.filters,
      sessionPageNumber,
    )
      .then((response) => {
        if (!active) return;
        setSessionPage(response);
        if (response.pagination.page !== sessionPageNumber) {
          setSessionPageNumber(response.pagination.page);
        }
        setSelectedSessionId(response.sessions[0]?.sessionId ?? null);
      })
      .catch((caught: unknown) => { if (active) setError(readError(caught)); })
      .finally(() => { if (active) setLoadingSessions(false); });
    return () => { active = false; };
  }, [props.environmentId, props.filters, selectedInstallationId, sessionPageNumber]);

  const installations = installationPage?.installations ?? [];
  const sessions = sessionPage?.sessions ?? [];
  const selectedInstallation = installations.find((installation) => installation.installationId === selectedInstallationId);
  const selectedSession = sessions.find((session) => session.sessionId === selectedSessionId);
  return (
    <section className="analytics-page">
      <AnalyticsFilterBar environmentId={props.environmentId} scope={props.filters} onChange={changeFilters} />
      {error !== '' && <div className="error-banner">{error}</div>}
      <section className="installation-explorer-layout">
        <article className="surface installation-list">
          <div className="surface-heading"><div><span className="eyebrow">Identities</span><h2>Known installs</h2></div><strong>{formatNumber(installationPage?.pagination.totalItems ?? 0)}</strong></div>
          {installations.map((installation) => <InstallationButton key={installation.installationId} installation={installation} active={installation.installationId === selectedInstallationId} onSelect={selectInstallation} />)}
          {installations.length === 0 && loadingInstallations && <Empty text="Loading installs…" compact />}
          {installations.length === 0 && !loadingInstallations && <Empty text="No installs were active within these filters." compact />}
          {installationPage !== null && <PaginationControls pagination={installationPage.pagination} disabled={loadingInstallations} onPageChange={changeInstallationPage} />}
        </article>
        <article className="surface installation-session-list">
          <div className="surface-heading"><div><span className="eyebrow">{selectedInstallation === undefined ? 'Install' : `Install ${shortId(selectedInstallation.installationId)}`}</span><h2>Sessions</h2></div><strong>{formatNumber(sessionPage?.pagination.totalItems ?? 0)}</strong></div>
          {selectedInstallation !== undefined && <InstallationContext installation={selectedInstallation} />}
          {sessions.map((session) => <SessionButton key={session.sessionId} session={session} active={session.sessionId === selectedSessionId} onSelect={setSelectedSessionId} />)}
          {selectedInstallationId === null && <Empty text="Choose an install to inspect its sessions." compact />}
          {selectedInstallationId !== null && sessions.length === 0 && loadingSessions && <Empty text="Loading sessions…" compact />}
          {selectedInstallationId !== null && sessions.length === 0 && !loadingSessions && <Empty text="This install has no recorded sessions." compact />}
          {sessionPage !== null && <PaginationControls pagination={sessionPage.pagination} disabled={loadingSessions} onPageChange={changeSessionPage} />}
        </article>
        <article className="surface event-stream installation-event-stream">
          <div className="surface-heading"><div><span className="eyebrow">Event timeline</span><h2>{selectedSession === undefined ? 'Choose a session' : `Session ${shortId(selectedSession.sessionId)}`}</h2></div><strong>{formatNumber(selectedSession?.eventCount ?? 0)} events</strong></div>
          {selectedSession !== undefined && <SessionNetworkContext session={selectedSession} />}
          {selectedSessionId === null && <Empty text="Choose a session to inspect its events in sequence." />}
          {selectedSessionId !== null && <PaginatedSessionEventTimeline key={selectedSessionId} environmentId={props.environmentId} sessionId={selectedSessionId} />}
        </article>
      </section>
    </section>
  );

  function changeFilters(filters: AnalyticsFilterScope): void {
    setInstallationPageNumber(1);
    setSessionPageNumber(1);
    props.onChangeFilters(filters);
  }

  function changeInstallationPage(page: number): void {
    setInstallationPageNumber(page);
    setSessionPageNumber(1);
  }

  function selectInstallation(installationId: string): void {
    setSessionPageNumber(1);
    setSelectedInstallationId(installationId);
  }

  function changeSessionPage(page: number): void {
    setSessionPageNumber(page);
  }
}

function InstallationButton(props: { installation: InstallationSummary; active: boolean; onSelect: (id: string) => void }): React.JSX.Element {
  const installation = props.installation;
  return <button className={props.active ? 'installation-button active' : 'installation-button'} onClick={() => props.onSelect(installation.installationId)} title={installation.installationId}><span><strong>{shortId(installation.installationId)}</strong><small>Last active {formatDate(installation.lastSeenAt)}</small><SessionCountry countryCode={installation.countryCode} countryCodes={installation.countryCodes} /><PlatformBadgeList codes={installation.platformCodes} /></span><span><strong>{formatNumber(installation.sessionCount)} sessions</strong><small>{formatNumber(installation.eventCount)} events</small><small>{installation.latestAppVersion}</small></span></button>;
}

function InstallationContext(props: { installation: InstallationSummary }): React.JSX.Element {
  const installation = props.installation;
  return <div className="installation-context"><code title={installation.installationId}>{installation.installationId}</code><span>First seen <strong>{formatDate(installation.firstSeenAt)}</strong></span><span>Last active <strong>{formatDate(installation.lastSeenAt)}</strong></span><span>Countries <strong><SessionCountry countryCode={installation.countryCode} countryCodes={installation.countryCodes} /></strong></span><span>First source <strong><PlatformBadge code={installation.firstPlatformCode} /> {installation.firstAppVersion}</strong></span><span>Latest source <strong><PlatformBadge code={installation.latestPlatformCode} /> {installation.latestAppVersion}</strong></span>{installation.sourceIps !== undefined && <span className="sensitive-network-detail">Source IPs <code>{installation.sourceIps.join(', ') || '—'}</code></span>}</div>;
}

function SessionButton(props: { session: SessionSummary; active: boolean; onSelect: (id: string) => void }): React.JSX.Element {
  const session = props.session;
  return <button className={props.active ? 'installation-session-button active' : 'installation-session-button'} onClick={() => props.onSelect(session.sessionId)} title={session.sessionId}><span><strong>#{session.sessionIndex} · {shortId(session.sessionId)}</strong><small>{formatDate(session.startedAt)}</small><SessionCountry countryCode={session.countryCode} countryCodes={session.countryCodes} /><PlatformBadge code={session.platformCode} /></span><span><strong>{formatNumber(session.eventCount)}</strong><small>{formatDuration(session.durationMs)}</small><small>{session.appVersion}</small></span></button>;
}

function SessionNetworkContext(props: { session: SessionSummary }): React.JSX.Element {
  return <div className="session-network-context"><SessionCountry countryCode={props.session.countryCode} countryCodes={props.session.countryCodes} />{props.session.sourceIps !== undefined && <span className="sensitive-network-detail">Source IPs <code>{props.session.sourceIps.join(', ') || '—'}</code></span>}</div>;
}

function Empty(props: { text: string; compact?: boolean }): React.JSX.Element {
  return <div className={props.compact === true ? 'empty compact' : 'empty'}>{props.text}</div>;
}

function shortId(value: string): string { return value.slice(0, 8); }
function formatNumber(value: number): string { return new Intl.NumberFormat().format(value); }
function formatDate(value: string): string { return formatDateTime(value); }
function formatDuration(milliseconds: number): string { const seconds = Math.round(milliseconds / 1000); return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`; }
